import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Users,
  UserCheck,
  UserX,
  Search,
  Mail,
  Building2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Filter,
  ChevronDown,
  AlertTriangle,
  Settings,
  Eye,
  Calendar,
  Briefcase,
  Package,
  FileText,
  DollarSign,
  Truck,
  BarChart3,
  X,
  Trash2,
  Ban,
  Power,
  Building,
  Plus,
} from "lucide-react";


// Define permission groups matching navigation menu structure
const PERMISSION_GROUPS = {
  admin: {
    name: "Admin",
    description: "Full system administration, APIs, SMS, Health Dashboard",
    icon: ShieldCheck,
    color: "red",
    modules: ["admin", "apis", "sms-management", "support-queue", "health-dashboard", "task-templates", "user-management", "import-export", "settings"],
  },
  operations: {
    name: "Operations",
    description: "PFs, Transport, BL, Trinet, Partner Networks, CRM",
    icon: Package,
    color: "blue",
    modules: ["operations", "position-folders", "transport-orders", "bl", "trinet", "partner-directory", "crm", "customer-groups", "email-repository", "trucking-blacklist", "trucking-portal"],
  },
  finance: {
    name: "Finance",
    description: "AR/AP, Cashflow, Bank Balances, Loans, Salaries",
    icon: DollarSign,
    color: "green",
    modules: ["finance", "vendor-ap", "ar-ap", "cashflow-dashboard", "bank-status", "loans", "cashflow", "izvrsbe"],
  },
  sales: {
    name: "Sales",
    description: "Offers, Quotes, Leads, Customer Advisory, Email Archive",
    icon: Briefcase,
    color: "purple",
    modules: ["sales", "offers", "rate-finder", "customer-advisory", "sales-leads", "email-archive"],
  },
  hr: {
    name: "HR & News",
    description: "Staff management, Calendar, Newsletter, LinkedIn, Reviews",
    icon: Users,
    color: "pink",
    modules: ["hr", "staff", "leave-calendar", "hr-performance", "my-feedback", "satisfaction-reviews", "news", "newsletter", "linkedin-newsletter"],
  },
  tools: {
    name: "Tools",
    description: "AI Assistant, Calculators, Voice Notes, User Guide",
    icon: Settings,
    color: "cyan",
    modules: ["tools", "chat", "co2-calculator", "load-calculator", "route-calculator", "voice-notes", "user-guide"],
  },
  tracking: {
    name: "Tracking",
    description: "Container tracking and shipment visibility",
    icon: Truck,
    color: "amber",
    modules: ["tracking", "core"],
  },
};

const STATUS_COLORS = {
  pending: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
  approved: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200" },
  rejected: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200" },
};

const USER_TYPE_LABELS = {
  europacific_staff: { label: "Staff", color: "blue" },
  customer: { label: "Customer", color: "slate" },
};

export default function UserManagementPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
  const [requestInfoMessage, setRequestInfoMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Pantheon customer linking state
  const [pantheonSearchQuery, setPantheonSearchQuery] = useState("");
  const [pantheonSearchResults, setPantheonSearchResults] = useState([]);
  const [selectedPantheonCustomer, setSelectedPantheonCustomer] = useState(null);
  // 2026-05-16: multi-company linking (Palfinger group branches etc.)
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [pantheonSearchLoading, setPantheonSearchLoading] = useState(false);
  
  // Edit customer modal state
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUsers();
    } else {
      setLoading(false);
      setError("No authentication token available");
    }
  }, [token]);

  // Search Pantheon customers with debounce
  useEffect(() => {
    const searchPantheon = async () => {
      if (!pantheonSearchQuery || pantheonSearchQuery.length < 2) {
        setPantheonSearchResults([]);
        return;
      }
      
      setPantheonSearchLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/auth/search-pantheon-customers`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { query: pantheonSearchQuery, limit: 20 }
          }
        );
        setPantheonSearchResults(response.data.companies || []);
      } catch (error) {
        console.error("Failed to search Pantheon customers:", error);
      } finally {
        setPantheonSearchLoading(false);
      }
    };
    
    const debounceTimer = setTimeout(searchPantheon, 300);
    return () => clearTimeout(debounceTimer);
  }, [pantheonSearchQuery, token]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all users
      const response = await axios.get(`${API_URL}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const allUsers = response.data.users || [];
      setUsers(allUsers);
      setPendingUsers(allUsers.filter(u => u.approval_status === "pending"));
    } catch (error) {
      console.error("Error fetching users:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to load users";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userToApprove) => {
    setSelectedUser(userToApprove);
    
    // For both staff and customers, show approval modal with options
    setSelectedGroups([]);
    setIsAdmin(false);
    setSelectedPantheonCustomer(null);
    // Pre-fill with the company the user registered with so it auto-searches
    // (and powers "Create & link" if it isn't in Pantheon/CRM yet).
    setPantheonSearchQuery(userToApprove?.company_name || "");
    setPantheonSearchResults([]);
    setShowPermissionsModal(true);
  };

  // Create a new local CRM company and select it for linking (used when the
  // company exists neither in Pantheon nor the CRM list yet).
  const createAndLinkCompany = async (rawName) => {
    const name = (rawName || "").trim();
    if (!name) return;
    setCreatingCompany(true);
    try {
      const res = await axios.post(
        `${API_URL}/crm/companies`,
        { name, country: selectedUser?.company_country || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const company = res.data;
      setSelectedPantheonCustomer({ id: company.id, name: company.name, country: company.country });
      setPantheonSearchResults([]);
      toast.success(`Created & linked "${company.name}"`);
    } catch (error) {
      // If it already exists (e.g., special chars made search miss it), link it.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.existing_id) {
        setSelectedPantheonCustomer({ id: detail.existing_id, name: detail.existing_name });
        setPantheonSearchResults([]);
        toast.success(`Linked existing company "${detail.existing_name}"`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create company");
      }
    } finally {
      setCreatingCompany(false);
    }
  };

  // Edit-modal variant: create a new local CRM company and ADD it to the
  // multi-company linked list.
  const createAndAddLinkedCompany = async (rawName) => {
    const name = (rawName || "").trim();
    if (!name) return;
    setCreatingCompany(true);
    try {
      const res = await axios.post(
        `${API_URL}/crm/companies`,
        { name, country: selectedUser?.company_country || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const company = res.data;
      setLinkedCompanies(prev =>
        prev.some(c => c.id === company.id)
          ? prev
          : [...prev, { id: company.id, name: company.name, country: company.country }]
      );
      setPantheonSearchQuery("");
      setPantheonSearchResults([]);
      toast.success(`Created & linked "${company.name}"`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.existing_id) {
        setLinkedCompanies(prev =>
          prev.some(c => c.id === detail.existing_id)
            ? prev
            : [...prev, { id: detail.existing_id, name: detail.existing_name }]
        );
        setPantheonSearchQuery("");
        setPantheonSearchResults([]);
        toast.success(`Linked existing company "${detail.existing_name}"`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create company");
      }
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleViewDetails = (userToView) => {
    setSelectedUser(userToView);
    setShowDetailsModal(true);
  };

  const handleRequestInfo = (userToRequest) => {
    setSelectedUser(userToRequest);
    setRequestInfoMessage(`Hello ${userToRequest.full_name || 'User'},\n\nThank you for registering with myEuropacific. Before we can approve your account, we need some additional information:\n\n- Company registration number / VAT ID\n- Company address\n- Your role/position\n- Brief description of your business\n\nPlease reply to this email with the requested information.\n\nBest regards,\nmyEuropacific Team`);
    setShowRequestInfoModal(true);
  };

  const handleSendRequestInfo = async () => {
    if (!selectedUser || !requestInfoMessage.trim()) return;
    
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/auth/request-more-info/${selectedUser.id}`,
        { message: requestInfoMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Information request sent to ${selectedUser.email}`);
      setShowRequestInfoModal(false);
      setSelectedUser(null);
      setRequestInfoMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveWithPermissions = async () => {
    if (!selectedUser) return;
    
    // For customers, use customer_portal permissions
    let modules;
    let adminStatus = false;
    
    if (selectedUser.user_type === 'customer') {
      modules = ['customer_portal'];
    } else {
      // Collect all modules from selected groups for staff
      const moduleSet = new Set();
      selectedGroups.forEach(groupKey => {
        PERMISSION_GROUPS[groupKey]?.modules.forEach(m => moduleSet.add(m));
      });
      modules = Array.from(moduleSet);
      adminStatus = isAdmin || selectedGroups.includes("admin");
    }
    
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/auth/approve-registration/${selectedUser.id}`,
        { 
          is_admin: adminStatus,
          module_permissions: modules,
          pantheon_customer_id: selectedPantheonCustomer?.id || null,
          pantheon_customer_name: selectedPantheonCustomer?.name || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const successMsg = selectedUser.user_type === 'customer' 
        ? `${selectedUser.email} approved as customer${selectedPantheonCustomer ? ` (linked to ${selectedPantheonCustomer.name})` : ''}`
        : `${selectedUser.email} approved with ${selectedGroups.join(", ")} permissions`;
      toast.success(successMsg);
      setShowPermissionsModal(false);
      setSelectedUser(null);
      setSelectedPantheonCustomer(null);
      setPantheonSearchQuery("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = (userToReject) => {
    setSelectedUser(userToReject);
    setRejectReason("");
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!selectedUser) return;
    
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/auth/reject-registration/${selectedUser.id}`,
        { reason: rejectReason || "No reason provided" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedUser.email} rejected`);
      setShowRejectModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject user");
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdatePermissions = async (userToUpdate) => {
    setSelectedUser(userToUpdate);
    
    // Pre-select groups based on existing permissions
    const existingModules = userToUpdate.module_permissions || [];
    const preselectedGroups = [];
    
    Object.entries(PERMISSION_GROUPS).forEach(([key, group]) => {
      const hasAllModules = group.modules.every(m => existingModules.includes(m));
      if (hasAllModules) preselectedGroups.push(key);
    });
    
    setSelectedGroups(preselectedGroups);
    setIsAdmin(userToUpdate.is_admin || false);
    setShowPermissionsModal(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    const modules = new Set();
    selectedGroups.forEach(groupKey => {
      PERMISSION_GROUPS[groupKey]?.modules.forEach(m => modules.add(m));
    });
    
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/auth/user/${selectedUser.id}/permissions`,
        { 
          is_admin: isAdmin || selectedGroups.includes("admin"),
          module_permissions: Array.from(modules)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Permissions updated for ${selectedUser.email}`);
      setShowPermissionsModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update permissions");
    } finally {
      setProcessing(false);
    }
  };

  const toggleGroup = (groupKey) => {
    setSelectedGroups(prev => 
      prev.includes(groupKey) 
        ? prev.filter(g => g !== groupKey)
        : [...prev, groupKey]
    );
  };

  // Handle edit customer (for linking/updating Pantheon connection)
  const handleEditCustomer = (customerUser) => {
    setSelectedUser(customerUser);
    // 2026-05-16: multi-company linking — pre-populate with the full
    // list of linked companies, falling back to the legacy single field.
    const linked = Array.isArray(customerUser.linked_companies) && customerUser.linked_companies.length > 0
      ? customerUser.linked_companies
      : (customerUser.linked_company_id
          ? [{ id: customerUser.linked_company_id, name: customerUser.linked_company_name || "" }]
          : []);
    setSelectedPantheonCustomer(linked.length === 1 ? linked[0] : null); // keep legacy single state in sync
    setLinkedCompanies(linked);
    setPantheonSearchQuery("");
    setPantheonSearchResults([]);
    setShowEditCustomerModal(true);
  };

  // Save customer Pantheon link
  const handleSaveCustomerLink = async () => {
    if (!selectedUser) return;
    
    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/auth/user/${selectedUser.id}/pantheon-link`,
        { 
          // New canonical field (server falls back to legacy if absent)
          linked_companies: linkedCompanies.map(c => ({ id: c.id, name: c.name })),
          // Legacy fields kept for back-compat with older builds
          pantheon_customer_id: linkedCompanies[0]?.id || null,
          pantheon_customer_name: linkedCompanies[0]?.name || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const linkMsg = linkedCompanies.length === 0
        ? "Pantheon link removed"
        : linkedCompanies.length === 1
          ? `Linked to ${linkedCompanies[0].name}`
          : `Linked to ${linkedCompanies.length} companies`;
      toast.success(`${selectedUser.full_name || selectedUser.email}: ${linkMsg}`);
      setShowEditCustomerModal(false);
      setSelectedUser(null);
      setSelectedPantheonCustomer(null);
      setLinkedCompanies([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer link");
    } finally {
      setProcessing(false);
    }
  };

  // Delete user handler
  const handleDeleteUser = (userToRemove) => {
    setUserToDelete(userToRemove);
    setShowDeleteModal(true);
  };

  const handleToggleDisabled = async (u) => {
    const willDisable = !u.disabled;
    if (willDisable && !window.confirm(
      `Disable ${u.email}?\n\nThey will be blocked from logging in and any active session ends immediately. All their data and history stay intact. You can re-enable them anytime.`
    )) return;
    try {
      await axios.put(
        `${API_URL}/auth/user/${u.id}/disabled`,
        { disabled: willDisable },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(willDisable ? `${u.email} has been disabled` : `${u.email} has been re-enabled`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/auth/user/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(`User ${userToDelete.email} has been deleted`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || u.approval_status === filterStatus;
    const matchesType = filterType === "all" || u.user_type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Failed to Load User Management</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchUsers} className="bg-red-600 hover:bg-red-700">
              <Loader2 className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            User Management
          </h1>
          <p className="text-slate-600 mt-1">
            Approve registrations and manage user permissions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <motion.div 
            className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-all ${activeTab === 'pending' ? 'ring-2 ring-amber-400' : ''}`}
            onClick={() => setActiveTab('pending')}
            whileHover={{ scale: 1.02 }}
            data-testid="pending-users-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{pendingUsers.length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-all ${activeTab === 'staff' ? 'ring-2 ring-blue-400' : ''}`}
            onClick={() => setActiveTab('staff')}
            whileHover={{ scale: 1.02 }}
            data-testid="staff-users-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Staff Members</p>
                <p className="text-2xl font-bold">{users.filter(u => u.user_type === 'europacific_staff' && u.approval_status === 'approved').length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-all ${activeTab === 'customers' ? 'ring-2 ring-green-400' : ''}`}
            onClick={() => setActiveTab('customers')}
            whileHover={{ scale: 1.02 }}
            data-testid="customer-users-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Customers</p>
                <p className="text-2xl font-bold">{users.filter(u => u.user_type === 'customer' && u.approval_status === 'approved').length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-all ${activeTab === 'all' ? 'ring-2 ring-slate-400' : ''}`}
            onClick={() => setActiveTab('all')}
            whileHover={{ scale: 1.02 }}
            data-testid="all-users-card"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pending Approvals Section */}
        {activeTab === 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {pendingUsers.length > 0 ? (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <h2 className="font-semibold text-amber-800">
                    {pendingUsers.length} Registration{pendingUsers.length !== 1 ? 's' : ''} Awaiting Approval
                  </h2>
                </div>
                <div className="divide-y">
                  {pendingUsers.map((pendingUser) => (
                    <div key={pendingUser.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          {/* Avatar */}
                          <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold ${
                            pendingUser.user_type === 'europacific_staff' 
                              ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                              : 'bg-gradient-to-br from-slate-400 to-slate-500'
                          }`}>
                            {pendingUser.full_name?.[0]?.toUpperCase() || pendingUser.email?.[0]?.toUpperCase()}
                          </div>
                          
                          {/* Info */}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">
                                {pendingUser.full_name || pendingUser.email}
                              </h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                pendingUser.user_type === 'europacific_staff'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {pendingUser.user_type === 'europacific_staff' ? 'Staff' : 'Customer'}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3.5 w-3.5" />
                                {pendingUser.email}
                              </span>
                              {pendingUser.company_name && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="h-3.5 w-3.5" />
                                  {pendingUser.company_name}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Registered: {formatDate(pendingUser.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(pendingUser)}
                            className="text-slate-600 border-slate-200 hover:bg-slate-50"
                            data-testid={`view-user-${pendingUser.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRequestInfo(pendingUser)}
                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                            data-testid={`request-info-${pendingUser.id}`}
                          >
                            <Mail className="h-4 w-4 mr-1" />
                            Request Info
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(pendingUser)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`reject-user-${pendingUser.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(pendingUser)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-user-${pendingUser.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
                <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">All Caught Up!</h3>
                <p className="text-slate-500">No pending registrations to review</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Staff/Customers/All Users Tab */}
        {activeTab !== 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Filters */}
            <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[250px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, email, company..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-400" />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="h-10 px-3 rounded-md border border-slate-200 text-sm"
                    data-testid="filter-status"
                  >
                    <option value="all">All Statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="h-10 px-3 rounded-md border border-slate-200 text-sm"
                    data-testid="filter-type"
                  >
                    <option value="all">All Types</option>
                    <option value="europacific_staff">Staff</option>
                    <option value="customer">Customers</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Permissions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Registered</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers
                      .filter(u => {
                        if (activeTab === 'staff') return u.user_type === 'europacific_staff';
                        if (activeTab === 'customers') return u.user_type === 'customer';
                        return true;
                      })
                      .map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                              u.user_type === 'europacific_staff' 
                                ? 'bg-blue-500' 
                                : 'bg-slate-400'
                            }`}>
                              {u.full_name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{u.full_name || "No name"}</p>
                              <p className="text-xs text-slate-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.user_type === 'europacific_staff'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {u.user_type === 'europacific_staff' ? 'Staff' : 'Customer'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[u.approval_status]?.bg} ${STATUS_COLORS[u.approval_status]?.text}`}>
                            {u.approval_status === 'approved' && <CheckCircle2 className="h-3 w-3 inline mr-1" />}
                            {u.approval_status === 'pending' && <Clock className="h-3 w-3 inline mr-1" />}
                            {u.approval_status === 'rejected' && <XCircle className="h-3 w-3 inline mr-1" />}
                            {u.approval_status?.charAt(0).toUpperCase() + u.approval_status?.slice(1)}
                          </span>
                          {u.disabled && (
                            <span className="ml-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid={`disabled-badge-${u.id}`}>
                              <Ban className="h-3 w-3 inline mr-1" />
                              Disabled
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {u.is_admin && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                Admin
                              </span>
                            )}
                            {u.module_permissions?.slice(0, 3).map(perm => (
                              <span key={perm} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                {perm}
                              </span>
                            ))}
                            {u.module_permissions?.length > 3 && (
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                +{u.module_permissions.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(u.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {u.approval_status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReject(u)}
                                  className="text-red-600 hover:bg-red-50"
                                  data-testid={`reject-${u.id}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleApprove(u)}
                                  className="text-green-600 hover:bg-green-50"
                                  data-testid={`approve-${u.id}`}
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {u.approval_status === 'rejected' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(u)}
                                className="text-green-600 hover:bg-green-50"
                                title="Re-approve user"
                                data-testid={`reapprove-${u.id}`}
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            )}
                            {u.approval_status === 'approved' && u.user_type === 'europacific_staff' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUpdatePermissions(u)}
                                title="Edit permissions"
                                data-testid={`edit-permissions-${u.id}`}
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            )}
                            {u.approval_status === 'approved' && u.user_type === 'customer' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCustomer(u)}
                                title="Edit customer / Link to Pantheon"
                                className="text-blue-600 hover:bg-blue-50"
                                data-testid={`edit-customer-${u.id}`}
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            )}
                            {/* Disable / Enable button - available for all users except self */}
                            {u.email !== user?.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleDisabled(u)}
                                title={u.disabled ? "Re-enable user (restore access)" : "Disable user (block app access, keep data)"}
                                className={u.disabled ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}
                                data-testid={`toggle-disabled-${u.id}`}
                              >
                                {u.disabled ? <Power className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                              </Button>
                            )}
                            {/* Delete button - available for all users except self */}
                            {u.email !== user?.email && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u)}
                                title="Delete user"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredUsers.length === 0 && (
                <div className="p-12 text-center">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No users found matching your filters</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Permissions Modal */}
        <AnimatePresence>
          {showPermissionsModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowPermissionsModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedUser.approval_status === 'pending' ? 'Approve User' : selectedUser.approval_status === 'rejected' ? 'Re-approve User' : 'Edit Permissions'}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{selectedUser.email}</p>
                  </div>
                  <button
                    onClick={() => setShowPermissionsModal(false)}
                    className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {/* User Info Summary */}
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        selectedUser.user_type === 'europacific_staff' 
                          ? 'bg-blue-500' 
                          : 'bg-slate-400'
                      }`}>
                        {selectedUser.full_name?.[0]?.toUpperCase() || selectedUser.email?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{selectedUser.full_name || selectedUser.email}</p>
                        <p className="text-sm text-slate-500">
                          {selectedUser.user_type === 'europacific_staff' ? 'Staff' : 'Customer'} • {selectedUser.company_name || 'No company'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer Portal Only for Customers */}
                  {selectedUser.user_type === 'customer' ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Users className="h-6 w-6 text-blue-600" />
                          <div>
                            <p className="font-medium text-blue-800">Customer Portal Access</p>
                            <p className="text-sm text-blue-600">
                              Customers get access to: Tracking, Quotes, Support Portal
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Pantheon Customer Linking */}
                      <div className="p-4 border border-slate-200 rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                          <Building className="h-5 w-5 text-slate-600" />
                          <div>
                            <p className="font-medium text-slate-800">Link to Company (Optional)</p>
                            <p className="text-xs text-slate-500">Connect this user to a Pantheon or local CRM company — or create a new one</p>
                          </div>
                        </div>
                        
                        {selectedPantheonCustomer ? (
                          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <Building className="h-5 w-5 text-green-600" />
                            <div className="flex-1">
                              <p className="font-medium text-green-800">{selectedPantheonCustomer.name}</p>
                              {selectedPantheonCustomer.country && (
                                <p className="text-xs text-green-600">{selectedPantheonCustomer.country}</p>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedPantheonCustomer(null);
                                setPantheonSearchQuery("");
                              }}
                              className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                placeholder="Search Pantheon or CRM companies..."
                                value={pantheonSearchQuery}
                                onChange={(e) => setPantheonSearchQuery(e.target.value)}
                                className="pl-10"
                                data-testid="pantheon-search-input"
                              />
                              {pantheonSearchLoading && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                              )}
                            </div>
                            
                            {pantheonSearchResults.length > 0 && (
                              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                                {pantheonSearchResults.map(company => (
                                  <button
                                    key={company.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedPantheonCustomer(company);
                                      setPantheonSearchQuery("");
                                      setPantheonSearchResults([]);
                                    }}
                                    className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors"
                                    data-testid={`pantheon-option-${company.id}`}
                                  >
                                    <Building className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-slate-800 truncate">{company.name}</p>
                                      <p className="text-xs text-slate-500 truncate">
                                        {company.country || "No country"} {company.email && `• ${company.email}`}
                                      </p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {pantheonSearchQuery.trim().length >= 2 && pantheonSearchResults.length === 0 && !pantheonSearchLoading && (
                              <div className="text-center py-2 space-y-2">
                                <p className="text-xs text-slate-500">
                                  No company found for {`"${pantheonSearchQuery}"`}
                                </p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => createAndLinkCompany(pantheonSearchQuery)}
                                  disabled={creatingCompany}
                                  data-testid="create-link-company-btn"
                                >
                                  {creatingCompany ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Plus className="h-4 w-4 mr-2" />
                                  )}
                                  Create & link {`"${pantheonSearchQuery.trim()}"`}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label className="text-slate-700 font-medium mb-3 block">Select Permission Groups</Label>
                        <p className="text-sm text-slate-500 mb-4">
                          Choose which modules this staff member can access
                        </p>
                        
                        <div className="grid gap-3">
                          {Object.entries(PERMISSION_GROUPS).map(([key, group]) => {
                            const Icon = group.icon;
                            const isSelected = selectedGroups.includes(key);
                            
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => toggleGroup(key)}
                                className={`p-4 rounded-lg border-2 text-left transition-all ${
                                  isSelected
                                    ? `border-${group.color}-400 bg-${group.color}-50`
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                                data-testid={`permission-group-${key}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                    isSelected ? `bg-${group.color}-100` : 'bg-slate-100'
                                  }`}>
                                    <Icon className={`h-5 w-5 ${isSelected ? `text-${group.color}-600` : 'text-slate-500'}`} />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <p className={`font-medium ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>
                                        {group.name}
                                      </p>
                                      {isSelected && (
                                        <CheckCircle2 className={`h-5 w-5 text-${group.color}-500`} />
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-500">{group.description}</p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {selectedGroups.length > 0 && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs font-medium text-slate-600 mb-2">Modules included:</p>
                          <div className="flex flex-wrap gap-1">
                            {Array.from(new Set(selectedGroups.flatMap(g => PERMISSION_GROUPS[g]?.modules || []))).map(m => (
                              <span key={m} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600">
                                {m}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowPermissionsModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={selectedUser.approval_status === 'pending' || selectedUser.approval_status === 'rejected' ? handleApproveWithPermissions : handleSavePermissions}
                    disabled={processing || (selectedUser.user_type === 'europacific_staff' && selectedGroups.length === 0)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-permissions-btn"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    {selectedUser.approval_status === 'pending' || selectedUser.approval_status === 'rejected'
                      ? (selectedUser.user_type === 'customer' ? 'Approve Customer' : 'Approve with Permissions')
                      : 'Save Permissions'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reject Modal */}
        <AnimatePresence>
          {showRejectModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowRejectModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b flex items-center gap-3">
                  <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Reject Registration</h2>
                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                  </div>
                </div>

                <div className="p-6">
                  <Label className="text-slate-700">Rejection Reason (optional)</Label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Provide a reason for rejection..."
                    className="w-full mt-2 p-3 rounded-lg border border-slate-200 text-sm resize-none h-24"
                    data-testid="reject-reason-input"
                  />
                </div>

                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleConfirmReject}
                    disabled={processing}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-reject-btn"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Reject User
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Details Modal */}
        <AnimatePresence>
          {showDetailsModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDetailsModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold ${
                      selectedUser.user_type === 'europacific_staff' 
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                        : 'bg-gradient-to-br from-slate-400 to-slate-500'
                    }`}>
                      {selectedUser.full_name?.[0]?.toUpperCase() || selectedUser.email?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{selectedUser.full_name || "No Name"}</h2>
                      <p className="text-sm text-slate-500">{selectedUser.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">User Type</Label>
                      <p className="font-medium">{selectedUser.user_type === 'europacific_staff' ? 'Staff' : 'Customer'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Status</Label>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedUser.approval_status]?.bg} ${STATUS_COLORS[selectedUser.approval_status]?.text}`}>
                        {selectedUser.approval_status?.charAt(0).toUpperCase() + selectedUser.approval_status?.slice(1)}
                      </span>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Company</Label>
                      <p className="font-medium">{selectedUser.company_name || "Not provided"}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Registered</Label>
                      <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                    </div>
                  </div>

                  {selectedUser.linked_company_id && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <Label className="text-xs text-green-600">Linked CRM Company</Label>
                      <p className="font-medium text-green-800">{selectedUser.linked_company_ref || selectedUser.linked_company_id}</p>
                    </div>
                  )}

                  {selectedUser.consents && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500 mb-2 block">Consents</Label>
                      <div className="space-y-1 text-sm">
                        <p className="flex items-center gap-2">
                          {selectedUser.consents.terms_accepted ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          Terms & Conditions
                        </p>
                        <p className="flex items-center gap-2">
                          {selectedUser.consents.gdpr_accepted ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          GDPR Privacy Policy
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedUser.module_permissions?.length > 0 && (
                    <div>
                      <Label className="text-xs text-slate-500 mb-2 block">Current Permissions</Label>
                      <div className="flex flex-wrap gap-1">
                        {selectedUser.module_permissions.map(perm => (
                          <span key={perm} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                    Close
                  </Button>
                  {selectedUser.approval_status === 'pending' && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowDetailsModal(false);
                          handleRequestInfo(selectedUser);
                        }}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Request Info
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowDetailsModal(false);
                          handleApprove(selectedUser);
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Request More Info Modal */}
        <AnimatePresence>
          {showRequestInfoModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowRequestInfoModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b flex items-center gap-3">
                  <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Request More Information</h2>
                    <p className="text-sm text-slate-500">Send email to {selectedUser.email}</p>
                  </div>
                </div>

                <div className="p-6">
                  <Label className="text-slate-700 mb-2 block">Email Message</Label>
                  <textarea
                    value={requestInfoMessage}
                    onChange={(e) => setRequestInfoMessage(e.target.value)}
                    placeholder="Enter your message..."
                    className="w-full p-3 rounded-lg border border-slate-200 text-sm resize-none h-48 font-mono"
                    data-testid="request-info-message"
                  />
                  <p className="text-xs text-slate-400 mt-2">
                    This email will be sent to the user requesting additional information before approval.
                  </p>
                </div>

                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowRequestInfoModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSendRequestInfo}
                    disabled={processing || !requestInfoMessage.trim()}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="send-request-info-btn"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Send Request
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete User Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && userToDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => !deleting && setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-center text-slate-900 mb-2">
                    Delete User
                  </h2>
                  <p className="text-center text-slate-600 mb-6">
                    Are you sure you want to delete <strong>{userToDelete.full_name || userToDelete.email}</strong>?
                    This action cannot be undone.
                  </p>
                  
                  <div className="bg-slate-50 rounded-lg p-3 mb-6">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                        userToDelete.user_type === 'europacific_staff' ? 'bg-blue-500' : 'bg-slate-400'
                      }`}>
                        {userToDelete.full_name?.[0]?.toUpperCase() || userToDelete.email?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{userToDelete.full_name || "No name"}</p>
                        <p className="text-sm text-slate-500">{userToDelete.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowDeleteModal(false)}
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      onClick={handleConfirmDelete}
                      disabled={deleting}
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Customer Modal - Link to Pantheon */}
        <AnimatePresence>
          {showEditCustomerModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowEditCustomerModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Edit Customer</h2>
                    <p className="text-sm text-slate-500 mt-1">{selectedUser.email}</p>
                  </div>
                  <button
                    onClick={() => setShowEditCustomerModal(false)}
                    className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {/* User Info Summary */}
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold bg-slate-400">
                        {selectedUser.full_name?.[0]?.toUpperCase() || selectedUser.email?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{selectedUser.full_name || selectedUser.email}</p>
                        <p className="text-sm text-slate-500">
                          Customer • {selectedUser.company_name || 'No company'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pantheon Customer Linking (multi-company) */}
                  <div className="p-4 border border-slate-200 rounded-lg space-y-3" data-testid="pantheon-link-block">
                    <div className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-slate-600" />
                      <div>
                        <p className="font-medium text-slate-800">Link to Pantheon Customers</p>
                        <p className="text-xs text-slate-500">Connect this user to one or more CRM companies (e.g. group branches). The user will see shipments for every linked company.</p>
                      </div>
                    </div>

                    {/* Already-linked companies — list with remove buttons */}
                    {linkedCompanies.length > 0 && (
                      <div className="space-y-1.5" data-testid="linked-companies-list">
                        {linkedCompanies.map((c, idx) => (
                          <div
                            key={c.id || idx}
                            className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg"
                          >
                            <Building className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-green-800 truncate">{c.name || c.id}</p>
                              {c.country && <p className="text-xs text-green-600">{c.country}</p>}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setLinkedCompanies(prev => prev.filter((_, i) => i !== idx));
                              }}
                              className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                              data-testid={`remove-linked-company-${idx}`}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Search to add another company */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-600">
                        {linkedCompanies.length > 0 ? "Add another company" : "Search and pick a company"}
                      </p>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search Pantheon companies..."
                          value={pantheonSearchQuery}
                          onChange={(e) => setPantheonSearchQuery(e.target.value)}
                          className="pl-10"
                          data-testid="edit-pantheon-search-input"
                        />
                        {pantheonSearchLoading && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                        )}
                      </div>

                      {pantheonSearchResults.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                          {pantheonSearchResults.map(company => {
                            const alreadyLinked = linkedCompanies.some(c => c.id === company.id);
                            return (
                              <button
                                key={company.id}
                                type="button"
                                disabled={alreadyLinked}
                                onClick={() => {
                                  if (alreadyLinked) return;
                                  setLinkedCompanies(prev => [...prev, {
                                    id: company.id, name: company.name, country: company.country,
                                  }]);
                                  setPantheonSearchQuery("");
                                  setPantheonSearchResults([]);
                                }}
                                className={`w-full p-3 text-left flex items-center gap-3 transition-colors ${alreadyLinked ? "opacity-50 cursor-not-allowed bg-slate-50" : "hover:bg-slate-50"}`}
                                data-testid={`edit-pantheon-option-${company.id}`}
                              >
                                <Building className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-slate-800 truncate">{company.name}</p>
                                  <p className="text-xs text-slate-500 truncate">
                                    {company.country || "No country"} {company.email && `• ${company.email}`}
                                  </p>
                                </div>
                                {alreadyLinked && (
                                  <span className="text-[11px] text-slate-400">already linked</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {pantheonSearchQuery.trim().length >= 2 && pantheonSearchResults.length === 0 && !pantheonSearchLoading && (
                        <div className="text-center py-2 space-y-2">
                          <p className="text-xs text-slate-500">
                            No company found for {`"${pantheonSearchQuery}"`}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => createAndAddLinkedCompany(pantheonSearchQuery)}
                            disabled={creatingCompany}
                            data-testid="edit-create-link-company-btn"
                          >
                            {creatingCompany ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Plus className="h-4 w-4 mr-2" />
                            )}
                            Create & link {`"${pantheonSearchQuery.trim()}"`}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowEditCustomerModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveCustomerLink}
                    disabled={processing}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-customer-link-btn"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export { UserManagementPage };
